import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Label from "../../label";
import Textbox from "../../form/input";
import { Color } from "../../../constants/color";
import { BreakpointsQuery } from "../../../constants/device";
import { Headquarter, UserPageResponse, User } from "../../../types";
import Button from "../../button/button";
import Suggerimento from "../../tooltip/suggerimento";
import { Icons } from "../../../constants/icons";
import React, {
  ChangeEvent,
  useEffect,
  useState,
  useCallback,
  FormEvent,
} from "react";
import { Select, SelectItem, SelectProvider } from "../../form/select";
import { Bounce, ToastContainer, toast } from "react-toastify";
import {
  avvisoUnico,
  messaggioDiErrore,
} from "../../../features/notifiche/avvisi";
import { useSelectStore, SelectLabel } from "@ariakit/react";
import { axiosInstance } from "../../../provider/authProvider";
import axios from "axios";
import { BE_BASE_URL } from "../../../utils/config";
import UserRow from "./userRow";

interface Props {
  show: boolean;
  edit: boolean;
  companyData: UserPageResponse | undefined;
  editCompanyData: (
    field: string,
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.FormEvent<HTMLButtonElement>
      | undefined,
    index: number,
    users?: Set<User>
  ) => void;
  updateNewCompanyData: (newAdmins: Set<User>) => void; // Nuovo callback
}

const MainContainer = styled.div<{
  $show: boolean;
}>`
  display: ${(props) => (props.$show ? "flex" : "none")};
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
`;

const Separator = styled.div`
  height: 0.1rem;
  background-color: ${Color.LIGHT_BLUE}; /* usa il colore desiderato */
  width: 30%; /* puoi regolare la larghezza in base al layout */
  margin-top: 3rem; /* aggiungi un margine in alto per separare gli elementi */
`;

const ButtonContainer = styled.div<{
  $edit: boolean;
}>`
  display: ${(props) => (props.$edit ? "unset" : "none")};
  margin-top: 30px;
`;

// Chi crea un utente non gli sceglie la password: la imposta l'utente dal link dell'email.
const NotaPassword = styled.p`
  max-width: 32rem;
  margin: 0.8rem 0 0;
  text-align: center;
  font-family: "Avenir Next Regular", sans-serif;
  font-size: 14px;
  color: ${Color.LIGHT_BLUE};
`;

interface RoleProps {
  id: number;
  description: string | "" | null;
}

const Users = ({
  show,
  edit,
  companyData,
  editCompanyData,
  updateNewCompanyData,
}: Props) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<Set<User>>();
  const [roles, setRoles] = useState<Map<number, RoleProps> | null>(null); // nuovo campo per il ruolo

  useEffect(() => {
    setUsers(companyData?.admins);
  }, [companyData]);

  const selectStyle = {
    "--padBlock": "0.3rem",
    border: "2px solid",
    borderRadius: "1rem",
    fontSize: "1rem",
    borderColor: Color.LIGHT_BLUE,
  } as React.CSSProperties;

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get<RoleProps[]>(
        `${BE_BASE_URL}/user/roles`
      );
      if (response.data) {
        const rolesMap: Map<number, RoleProps> = new Map();
        response.data.forEach((r) =>
          rolesMap.set(r.id, { id: r.id, description: r.description })
        );
        setRoles(rolesMap);
      }
    } catch (e) {
      avvisoUnico(
        "utenti-ruoli",
        "error",
        messaggioDiErrore(e, "Errore durante il recupero dei ruoli.")
      );
    }
  }, []);

  const renderRolesItems = useCallback(
    (rolesMap: Map<number, RoleProps>) =>
      Array.from(rolesMap.entries()).map(([key, value]) => (
        <SelectItem key={`role_${key}`} value={`${key}`}>
          {value.description}
        </SelectItem>
      )),
    []
  );

  // Modificare la funzione handleDeleteUser nel componente Users

  const handleDeleteUser = async (user: User) => {
    // Verifica se l'utente ha un ID valido (diverso da 0)
    if (user.id === 0) {
      toast.warning(
        t("user-delete-not-created-warning") ||
          "Non è possibile eliminare un utente che non è ancora stato creato",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        }
      );
      return;
    }

    try {
      await axiosInstance.delete(`${BE_BASE_URL}/user`, {
        data: user, // Invia i dati come corpo della richiesta
      });

      toast.success("Utente eliminato con successo", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });

      // Crea una copia dell'utente con isDeleted=true
      const deletedUser = { ...user, isDeleted: true };

      // Aggiorna lo stato locale con l'utente marcato come eliminato
      const updatedUsers = new Set(
        Array.from(users!).map((u) => (u.id === user.id ? deletedUser : u))
      );
      setUsers(updatedUsers);

      // Aggiorna anche lo stato nel componente padre
      // Nota: includiamo anche gli utenti eliminati ma con il flag isDeleted
      const updatedAdmins = new Set(
        Array.from(companyData!.admins).map((u) =>
          u.id === user.id ? deletedUser : u
        )
      );
      updateNewCompanyData(updatedAdmins);
    } catch (err) {
      toast.error(
        messaggioDiErrore(err, "Errore durante l'eliminazione dell'utente"),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        }
      );
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return (
    <MainContainer $show={show}>
      <Label
        text={t("user-users-label")}
        fontSize="26px"
        fontWeight="bold"
        textTransform="uppercase"
      />
      {users &&
        Array.from(users).map((user, index, arr) => (
          <React.Fragment key={index}>
            <UserRow
              key={index}
              user={user}
              index={index}
              edit={edit}
              users={users}
              editCompanyData={editCompanyData}
              renderRolesSelectItems={renderRolesItems}
              roles={roles}
              handleDeleteUser={handleDeleteUser}
            />
            {index !== arr.length - 1 && <Separator />}
          </React.Fragment>
        ))}
      <ButtonContainer $edit={edit}>
        <Suggerimento testo={t("user-aiuto-aggiungi-utente")}>
          <Button
            color={Color.LIGHT_BLUE}
            textColor={Color.WHITE}
            icon={Icons.BUTTONS.PLUS}
            label={t("user-add-user")}
            onClick={() => {
              const user: User = {
                id: 0,
                firstName: "",
                lastName: "",
                email: "",
                role: 0,
                username: "",
                companyId: 0,
                isDeleted: false,
              };
              const newUsers = Array.from(users!);
              newUsers.push(user);
              setUsers(new Set(newUsers));
            }}
          />
        </Suggerimento>
      </ButtonContainer>
      {edit && <NotaPassword>{t("user-nuovo-utente-password-via-email")}</NotaPassword>}
    </MainContainer>
  );
};

export default Users;
